import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../configs/firebase';
import { AuthData } from '../../provider/AuthProvider';
import InternIDDisplay, { ProfileInternID } from '../../components/InternIDDisplay';
import { FaUser, FaGraduationCap, FaBriefcase, FaMapMarkerAlt, FaEnvelope, FaPhone, FaLinkedin, FaGithub, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';

const Profile = () => {
  const { user } = AuthData();
  const [profile, setProfile] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      location: '',
      bio: '',
      dateOfBirth: '',
      profilePicture: ''
    },
    education: {
      university: '',
      degree: '',
      major: '',
      graduationYear: '',
      gpa: '',
      relevantCourses: []
    },
    experience: [],
    skills: [],
    interests: [],
    social: {
      linkedin: '',
      github: '',
      portfolio: ''
    },
    preferences: {
      jobTypes: [],
      preferredLocations: [],
      salaryExpectation: '',
      availability: ''
    }
  });
  
  const [editing, setEditing] = useState({
    personalInfo: false,
    education: false,
    experience: false,
    skills: false,
    interests: false,
    social: false,
    preferences: false
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [newInterest, setNewInterest] = useState('');
  const [newExperience, setNewExperience] = useState({
    title: '',
    company: '',
    startDate: '',
    endDate: '',
    description: '',
    current: false
  });

  // Predefined options
  const skillSuggestions = [
    'JavaScript', 'Python', 'React', 'Node.js', 'Java', 'C++', 'HTML/CSS',
    'SQL', 'Git', 'AWS', 'Docker', 'Machine Learning', 'Data Analysis',
    'Project Management', 'Marketing', 'Design', 'Communication', 'Leadership'
  ];

  const interestAreas = [
    'Software Development', 'Data Science', 'Machine Learning', 'Cybersecurity',
    'Web Development', 'Mobile Development', 'DevOps', 'UI/UX Design',
    'Marketing', 'Sales', 'Finance', 'Consulting', 'Research', 'Healthcare',
    'Education', 'Non-profit', 'Sustainability', 'Entrepreneurship'
  ];

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.userd?.uid) return;
      
      try {
        const profileRef = doc(db, 'users', user.userd.uid);
        const profileSnap = await getDoc(profileRef);
        
        if (profileSnap.exists()) {
          const data = profileSnap.data();
          setProfile(prevProfile => ({
            ...prevProfile,
            ...data
          }));
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  // Save profile section
  const saveSection = async (section) => {
    if (!user?.userd?.uid) return;
    
    setSaving(true);
    try {
      const profileRef = doc(db, 'users', user.userd.uid);
      await setDoc(profileRef, profile, { merge: true });
      
      setEditing(prev => ({
        ...prev,
        [section]: false
      }));
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const cancelEdit = (section) => {
    setEditing(prev => ({
      ...prev,
      [section]: false
    }));
    // Reset form data here if needed
  };

  // Update profile field
  const updateField = (section, field, value) => {
    setProfile(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Add skill
  const addSkill = () => {
    if (newSkill.trim() && !profile.skills.includes(newSkill.trim())) {
      setProfile(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  // Remove skill
  const removeSkill = (skillToRemove) => {
    setProfile(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  // Add interest
  const addInterest = () => {
    if (newInterest.trim() && !profile.interests.includes(newInterest.trim())) {
      setProfile(prev => ({
        ...prev,
        interests: [...prev.interests, newInterest.trim()]
      }));
      setNewInterest('');
    }
  };

  // Remove interest
  const removeInterest = (interestToRemove) => {
    setProfile(prev => ({
      ...prev,
      interests: prev.interests.filter(interest => interest !== interestToRemove)
    }));
  };

  // Add experience
  const addExperience = () => {
    if (newExperience.title && newExperience.company) {
      setProfile(prev => ({
        ...prev,
        experience: [...prev.experience, { ...newExperience, id: Date.now() }]
      }));
      setNewExperience({
        title: '',
        company: '',
        startDate: '',
        endDate: '',
        description: '',
        current: false
      });
    }
  };

  // Remove experience
  const removeExperience = (experienceId) => {
    setProfile(prev => ({
      ...prev,
      experience: prev.experience.filter(exp => exp.id !== experienceId)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const SectionHeader = ({ title, section, icon: Icon }) => (
    <div className="flex justify-between items-center mb-4">
      <div className="flex items-center">
        <Icon className="text-orange-500 mr-2" />
        <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
      </div>
      <div className="flex space-x-2">
        {editing[section] ? (
          <>
            <button
              onClick={() => saveSection(section)}
              disabled={saving}
              className="flex items-center px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
            >
              <FaSave className="mr-1" />
              Save
            </button>
            <button
              onClick={() => cancelEdit(section)}
              className="flex items-center px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              <FaTimes className="mr-1" />
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={() => setEditing(prev => ({ ...prev, [section]: true }))}
            className="flex items-center px-3 py-1 bg-orange-500 text-white rounded-md hover:bg-orange-600"
          >
            <FaEdit className="mr-1" />
            Edit
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 pt-10 pb-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Email Verification Banner - Shows site-wide for unverified users */}
        {user?.isAuthenticated && (
            <EmailVerificationBanner 
            className="mx-4 mt-4 sm:mx-6 lg:mx-8" 
            dismissible={true}
            showOnlyIfUnverified={true}
            />
        )}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-8 py-12">
            <div className="flex items-center">
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center">
                <FaUser className="text-4xl text-gray-600" />
              </div>
              <div className="ml-6 text-white">
                <h1 className="text-3xl font-bold">
                  {profile.personalInfo.firstName} {profile.personalInfo.lastName}
                </h1>
                <p className="text-orange-100">{profile.personalInfo.email}</p>
                <p className="text-orange-100">{profile.personalInfo.location}</p>
                <div className="mt-2">
                  <ProfileInternID />
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            <div className="border-b pb-8">
              <InternIDDisplay />
            </div>
            {/* Personal Information */}
            <div className="border-b pb-8">
              <SectionHeader title="Personal Information" section="personalInfo" icon={FaUser} />
              
              {editing.personalInfo ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      value={profile.personalInfo.firstName}
                      onChange={(e) => updateField('personalInfo', 'firstName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={profile.personalInfo.lastName}
                      onChange={(e) => updateField('personalInfo', 'lastName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={profile.personalInfo.email}
                      onChange={(e) => updateField('personalInfo', 'email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={profile.personalInfo.phone}
                      onChange={(e) => updateField('personalInfo', 'phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input
                      type="text"
                      value={profile.personalInfo.location}
                      onChange={(e) => updateField('personalInfo', 'location', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={profile.personalInfo.dateOfBirth}
                      onChange={(e) => updateField('personalInfo', 'dateOfBirth', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    <div className="relative">
                      <textarea
                        value={profile.personalInfo.bio}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 300) {
                            updateField('personalInfo', 'bio', value);
                          }
                        }}
                        rows={4}
                        maxLength={300}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                        placeholder="Tell us about yourself, career goals, and what you're looking for in an internship..."
                      />
                      <div className="absolute bottom-2 right-2 text-xs text-gray-500 bg-white px-2 py-1 rounded shadow-sm">
                        {(profile.personalInfo.bio || '').length}/300
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-xs text-gray-500">
                        Help employers understand your background and interests
                      </p>
                      {(profile.personalInfo.bio || '').length >= 280 && (
                        <span className="text-xs font-medium text-orange-600">
                          {300 - (profile.personalInfo.bio || '').length} characters left
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                  <div className="flex items-center">
                    <FaEnvelope className="mr-2 text-gray-400" />
                    <span>{profile.personalInfo.email || 'Not provided'}</span>
                  </div>
                  <div className="flex items-center">
                    <FaPhone className="mr-2 text-gray-400" />
                    <span>{profile.personalInfo.phone || 'Not provided'}</span>
                  </div>
                  <div className="flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-gray-400" />
                    <span>{profile.personalInfo.location || 'Not provided'}</span>
                  </div>
                  <div className="md:col-span-2 mt-2">
                    <p className="text-gray-700">{profile.personalInfo.bio || 'No bio provided yet.'}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Education */}
            <div className="border-b pb-8">
              <SectionHeader title="Education" section="education" icon={FaGraduationCap} />
              
              {editing.education ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">University</label>
                    <input
                      type="text"
                      value={profile.education.university}
                      onChange={(e) => updateField('education', 'university', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Degree</label>
                    <input
                      type="text"
                      value={profile.education.degree}
                      onChange={(e) => updateField('education', 'degree', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Major</label>
                    <input
                      type="text"
                      value={profile.education.major}
                      onChange={(e) => updateField('education', 'major', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Year</label>
                    <input
                      type="number"
                      value={profile.education.graduationYear}
                      onChange={(e) => updateField('education', 'graduationYear', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GPA</label>
                    <input
                      type="text"
                      value={profile.education.gpa}
                      onChange={(e) => updateField('education', 'gpa', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-gray-700">
                  <p><strong>University:</strong> {profile.education.university || 'Not provided'}</p>
                  <p><strong>Degree:</strong> {profile.education.degree || 'Not provided'}</p>
                  <p><strong>Major:</strong> {profile.education.major || 'Not provided'}</p>
                  <p><strong>Graduation Year:</strong> {profile.education.graduationYear || 'Not provided'}</p>
                  <p><strong>GPA:</strong> {profile.education.gpa || 'Not provided'}</p>
                </div>
              )}
            </div>

            {/* Experience */}
            <div className="border-b pb-8">
              <SectionHeader title="Experience" section="experience" icon={FaBriefcase} />
              
              {editing.experience ? (
                <div className="space-y-4">
                  {/* Add new experience form */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-3">Add Experience</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Job Title"
                        value={newExperience.title}
                        onChange={(e) => setNewExperience(prev => ({ ...prev, title: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <input
                        type="text"
                        placeholder="Company"
                        value={newExperience.company}
                        onChange={(e) => setNewExperience(prev => ({ ...prev, company: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <input
                        type="date"
                        placeholder="Start Date"
                        value={newExperience.startDate}
                        onChange={(e) => setNewExperience(prev => ({ ...prev, startDate: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <input
                        type="date"
                        placeholder="End Date"
                        value={newExperience.endDate}
                        onChange={(e) => setNewExperience(prev => ({ ...prev, endDate: e.target.value }))}
                        disabled={newExperience.current}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
                      />
                      <div className="md:col-span-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newExperience.current}
                            onChange={(e) => setNewExperience(prev => ({ ...prev, current: e.target.checked }))}
                            className="mr-2"
                          />
                          Current Position
                        </label>
                      </div>
                      <div className="md:col-span-2">
                        <textarea
                          placeholder="Description"
                          value={newExperience.description}
                          onChange={(e) => setNewExperience(prev => ({ ...prev, description: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                      <button
                        onClick={addExperience}
                        className="bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600"
                      >
                        Add Experience
                      </button>
                    </div>
                  </div>
                  
                  {/* Existing experiences */}
                  <div className="space-y-3">
                    {profile.experience.map((exp, index) => (
                      <div key={exp.id || index} className="bg-white border border-gray-200 p-4 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-800">{exp.title}</h4>
                            <p className="text-gray-600">{exp.company}</p>
                            <p className="text-sm text-gray-500">
                              {exp.startDate} - {exp.current ? 'Present' : exp.endDate}
                            </p>
                            <p className="text-gray-700 mt-2">{exp.description}</p>
                          </div>
                          <button
                            onClick={() => removeExperience(exp.id)}
                            className="text-red-500 hover:text-red-700 ml-2"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {profile.experience.length === 0 ? (
                    <p className="text-gray-500">No experience added yet.</p>
                  ) : (
                    profile.experience.map((exp, index) => (
                      <div key={exp.id || index} className="border-l-4 border-orange-500 pl-4">
                        <h4 className="font-medium text-gray-800">{exp.title}</h4>
                        <p className="text-gray-600">{exp.company}</p>
                        <p className="text-sm text-gray-500">
                          {exp.startDate} - {exp.current ? 'Present' : exp.endDate}
                        </p>
                        <p className="text-gray-700 mt-2">{exp.description}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Skills */}
            <div className="border-b pb-8">
              <SectionHeader title="Skills" section="skills" icon={FaBriefcase} />
              
              {editing.skills ? (
                <div>
                  <div className="flex mb-4">
                    <input
                      type="text"
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      placeholder="Add a skill"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                    />
                    <button
                      onClick={addSkill}
                      className="bg-orange-500 text-white px-4 py-2 rounded-r-md hover:bg-orange-600"
                    >
                      Add
                    </button>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">Suggested skills:</p>
                    <div className="flex flex-wrap gap-2">
                      {skillSuggestions.filter(skill => !profile.skills.includes(skill)).map((skill, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setProfile(prev => ({
                              ...prev,
                              skills: [...prev.skills, skill]
                            }));
                          }}
                          className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full hover:bg-orange-100"
                        >
                          + {skill}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
              
              <div className="flex flex-wrap gap-2">
                {profile.skills.length === 0 ? (
                  <p className="text-gray-500">No skills added yet.</p>
                ) : (
                  profile.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center"
                    >
                      {skill}
                      {editing.skills && (
                        <button
                          onClick={() => removeSkill(skill)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          <FaTimes className="text-xs" />
                        </button>
                      )}
                    </span>
                  ))
                )}
              </div>
            </div>

            {/* Interests */}
            <div className="border-b pb-8">
              <SectionHeader title="Interests" section="interests" icon={FaBriefcase} />
              
              {editing.interests ? (
                <div>
                  <div className="flex mb-4">
                    <input
                      type="text"
                      value={newInterest}
                      onChange={(e) => setNewInterest(e.target.value)}
                      placeholder="Add an interest"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      onKeyPress={(e) => e.key === 'Enter' && addInterest()}
                    />
                    <button
                      onClick={addInterest}
                      className="bg-orange-500 text-white px-4 py-2 rounded-r-md hover:bg-orange-600"
                    >
                      Add
                    </button>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">Popular interest areas:</p>
                    <div className="flex flex-wrap gap-2">
                      {interestAreas.filter(interest => !profile.interests.includes(interest)).map((interest, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setProfile(prev => ({
                              ...prev,
                              interests: [...prev.interests, interest]
                            }));
                          }}
                          className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full hover:bg-orange-100"
                        >
                          + {interest}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
              
              <div className="flex flex-wrap gap-2">
                {profile.interests.length === 0 ? (
                  <p className="text-gray-500">No interests added yet.</p>
                ) : (
                  profile.interests.map((interest, index) => (
                    <span
                      key={index}
                      className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center"
                    >
                      {interest}
                      {editing.interests && (
                        <button
                          onClick={() => removeInterest(interest)}
                          className="ml-2 text-green-600 hover:text-green-800"
                        >
                          <FaTimes className="text-xs" />
                        </button>
                      )}
                    </span>
                  ))
                )}
              </div>
            </div>

            {/* Social Links */}
            <div>
              <SectionHeader title="Social Links" section="social" icon={FaLinkedin} />
              
              {editing.social ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                        <FaLinkedin />
                      </span>
                      <input
                        type="url"
                        value={profile.social.linkedin}
                        onChange={(e) => updateField('social', 'linkedin', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="https://linkedin.com/in/username"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GitHub</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                        <FaGithub />
                      </span>
                      <input
                        type="url"
                        value={profile.social.github}
                        onChange={(e) => updateField('social', 'github', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="https://github.com/username"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Portfolio Website</label>
                    <input
                      type="url"
                      value={profile.social.portfolio}
                      onChange={(e) => updateField('social', 'portfolio', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="https://yourportfolio.com"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-gray-700">
                  {profile.social.linkedin && (
                    <div className="flex items-center">
                      <FaLinkedin className="mr-2 text-blue-600" />
                      <a href={profile.social.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        LinkedIn Profile
                      </a>
                    </div>
                  )}
                  {profile.social.github && (
                    <div className="flex items-center">
                      <FaGithub className="mr-2 text-gray-800" />
                      <a href={profile.social.github} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        GitHub Profile
                      </a>
                    </div>
                  )}
                  {profile.social.portfolio && (
                    <div className="flex items-center">
                      <FaBriefcase className="mr-2 text-orange-500" />
                      <a href={profile.social.portfolio} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Portfolio Website
                      </a>
                    </div>
                  )}
                  {!profile.social.linkedin && !profile.social.github && !profile.social.portfolio && (
                    <p className="text-gray-500">No social links added yet.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;